/**
 * Vacation Rental structured-data injector for Duda dynamic property pages.
 *
 * Loaded site-wide on Plus customer sites via a Duda snippet. On dynamic pages
 * bound to the Uplisting PropertiesCollection, builds a schema.org VacationRental
 * JSON-LD payload from the collection row and appends it to <head>.
 *
 * Runs synchronously: pageData() reads a base64 row blob already in the page DOM.
 */

export interface Photo {
  url?: string
  order?: number
}

export interface Amenity {
  name?: string
  icon?: string
}

export interface PropertyRow {
  name?: string
  slug?: string
  description?: string
  type?: string
  primary_photo_url?: string
  photos?: Photo[]
  latitude?: number | string
  longitude?: number | string
  city?: string
  state?: string
  country?: string
  check_in_time?: string
  check_out_time?: string
  bedrooms?: number | string
  baths?: number | string
  beds?: number | string
  maximum_capacity?: number | string
  amenities?: Amenity[]
  price?: number | string
  currency_symbol?: string
}

export const stripHtml = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export const toNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const collectImages = (row: PropertyRow): string[] => {
  const ordered = (row.photos ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(p => p.url)
    .filter((url): url is string => Boolean(url))

  if (row.primary_photo_url && !ordered.includes(row.primary_photo_url)) {
    return [row.primary_photo_url, ...ordered]
  }
  return ordered
}

const AMENITY_MAP: Record<string, string> = {
  'Air Conditioning': 'ac',
  Balcony: 'balcony',
  Beach: 'beachAccess',
  Beachfront: 'privateBeachAccess',
  Elevator: 'elevator',
  Fireplace: 'fireplace',
  'Fitness Room / Equipment': 'gymFitnessEquipment',
  'Free wifi': 'wifi',
  Heating: 'heating',
  'Hot Tub': 'hotTub',
  Internet: 'wifi',
  'Iron & Board': 'ironingBoard',
  Kitchen: 'kitchen',
  Kitchenette: 'kitchen',
  Microwave: 'microwave',
  'Outdoor Grill': 'outdoorGrill',
  Oven: 'ovenStove',
  Stove: 'ovenStove',
  'Deck / Patio': 'patio',
  'Private Pool': 'pool',
  'Indoor Pool': 'pool',
  'Heated Pool': 'pool',
  'Communal Pool': 'pool',
  Television: 'tv',
  'Washing Machine': 'washerDryer',
  'Clothes Dryer': 'washerDryer',
  'Free parking on premises': 'parkingType',
  'Free street parking': 'parkingType',
  'Paid parking off premises': 'parkingType',
  'Paid parking on premises': 'parkingType',
  Parking: 'parkingType',
}

const PARKING_VALUE: Record<string, string> = {
  'Free parking on premises': 'Free',
  'Free street parking': 'Free',
  'Paid parking off premises': 'Paid',
  'Paid parking on premises': 'Paid',
  Parking: 'Free',
}

export const buildAmenityFeatures = (amenities: Amenity[] | undefined) => {
  if (!amenities?.length) return undefined

  const seen = new Set<string>()
  const features: { '@type': string; name: string; value: boolean | string }[] =
    []

  for (const a of amenities) {
    if (!a.name) continue
    const mapped = AMENITY_MAP[a.name]
    if (!mapped || seen.has(mapped)) continue
    seen.add(mapped)

    if (mapped === 'parkingType') {
      features.push({
        '@type': 'LocationFeatureSpecification',
        name: 'parkingType',
        value: PARKING_VALUE[a.name] || 'Free',
      })
    } else {
      features.push({
        '@type': 'LocationFeatureSpecification',
        name: mapped,
        value: true,
      })
    }
  }

  return features.length ? features : undefined
}

export const buildBedDetails = (beds: unknown) => {
  const count = toNumber(beds)
  if (count === undefined) return undefined
  return { '@type': 'BedDetails', numberOfBeds: count }
}

const VACATION_RENTAL_TYPE: Record<string, string> = {
  Aparthotel: 'Apartment',
  Apartment: 'Apartment',
  Bungalow: 'Bungalow',
  Cabin: 'Cabin',
  Chalet: 'Chalet',
  Condominium: 'Apartment',
  Cottage: 'Cottage',
  'Cycladic house (Greece)': 'House',
  'Dome house': 'House',
  'Earth house': 'House',
  Guesthouse: 'House',
  House: 'House',
  Loft: 'Apartment',
  'Serviced apartment': 'Apartment',
  Studio: 'Apartment',
  'Tiny house': 'House',
  Townhouse: 'House',
  Villa: 'Villa',
}

export const toSchemaOrgType = (
  type: string | undefined
): string | undefined => {
  if (!type) return undefined
  return VACATION_RENTAL_TYPE[type]
}

export const buildPriceRange = (row: PropertyRow): string | undefined => {
  const price = toNumber(row.price)
  if (price === undefined) return undefined
  const symbol = row.currency_symbol || '$'
  return `${symbol}${price}`
}

export const buildSchema = (row: PropertyRow) => {
  const occupancy = toNumber(row.maximum_capacity)
  const amenityFeature = buildAmenityFeatures(row.amenities)
  const bed = buildBedDetails(row.beds)
  const schemaType = toSchemaOrgType(row.type)

  return {
    '@context': 'https://schema.org',
    '@type': 'VacationRental',
    name: row.name,
    identifier: row.slug,
    description: stripHtml(row.description),
    additionalType: schemaType,
    image: collectImages(row),
    latitude: toNumber(row.latitude),
    longitude: toNumber(row.longitude),
    priceRange: buildPriceRange(row),
    address: {
      '@type': 'PostalAddress',
      addressLocality: row.city,
      addressRegion: row.state,
      addressCountry: row.country,
    },
    checkinTime: row.check_in_time,
    checkoutTime: row.check_out_time,
    containsPlace: {
      '@type': 'Accommodation',
      additionalType: 'EntirePlace',
      occupancy:
        occupancy === undefined
          ? undefined
          : { '@type': 'QuantitativeValue', value: occupancy },
      numberOfBedrooms: toNumber(row.bedrooms),
      numberOfBathroomsTotal: toNumber(row.baths),
      bed,
      amenityFeature,
    },
  }
}

const inject = (payload: object) => {
  document
    .querySelector('script[data-uplisting-vacation-rental-schema]')
    ?.remove()
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.setAttribute('data-uplisting-vacation-rental-schema', '')
  script.textContent = JSON.stringify(payload)
  document.head.appendChild(script)
}

// `dmAPI.dynamicPageApi` is provided by Duda's full runtime bundle (d-js-runtime),
// which loads after the inline bootstrap dmAPI. `runOnReady` waits for the runtime
// to finish initialising — at which point dynamicPageApi is available.
if (typeof dmAPI !== 'undefined' && typeof dmAPI.runOnReady === 'function') {
  dmAPI.runOnReady('vacationRentalSchema', () => {
    try {
      if (typeof dmAPI.dynamicPageApi !== 'function') return

      const dyn = dmAPI.dynamicPageApi()
      if (!dyn.isDynamicPage()) return

      const result = dyn.pageData<PropertyRow>()
      const apply = (row: PropertyRow) => {
        if (!row || !row.name) return
        inject(buildSchema(row))
      }

      if (
        result &&
        typeof (result as Promise<PropertyRow>).then === 'function'
      ) {
        ;(result as Promise<PropertyRow>).then(apply).catch(() => {})
      } else {
        apply(result as PropertyRow)
      }
    } catch {
      // Swallow: schema injection must never break the page.
    }
  })
}
